import { unwrap, type components } from "@sagot/api-client";
import { toast } from "@sagot/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceId } from "../../../../session/workspace-context";

type Schemas = components["schemas"];
export type LaunchSettings = Schemas["LaunchSettingsOut"];
export type LaunchSettingsPatch = Schemas["LaunchSettingsIn"];
export type SandboxLaunchIn = Schemas["SandboxLaunchIn"];
export type SandboxLaunch = Schemas["SandboxLaunchOut"];
export type SandboxLaunchStatus = Schemas["SandboxLaunchStatusOut"];

const STALE_MS = 30_000;
const POLL_MS = 2_000;

export const launchSettingsKey = (wsId: string) => ["ws", wsId, "settings", "launch-settings"] as const;
const sandboxKey = (wsId: string, launchId: string) => ["ws", wsId, "settings", "launch-settings", "test-launch", launchId] as const;

/**
 * `GET /v1/launch-settings` (M29 E16): the Secure launch settings, plus `enabled` (the platform flag for this workspace) and
 * `entitled` (the plan or the add-on includes it). It answers 200 while the flag is off, so reading it never logs a failed
 * request; the Settings menu and the page hide or explain Secure launch from these two answers.
 */
export function useLaunchSettings(enabled = true) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: launchSettingsKey(wsId), queryFn: () => unwrap(api.GET("/v1/launch-settings")), enabled, retry: false, staleTime: STALE_MS });
}

/**
 * `PUT /v1/launch-settings` (E17) for one card: sends only that card's fields, puts the answer in the cache so every card
 * shows the saved state, and keeps the failure text for the card to show next to its draft.
 */
export function useSaveLaunchSettings(savedMessage: string) {
  const api = useApi();
  const wsId = useWorkspaceId();
  const queryClient = useQueryClient();
  const message = useErrorMessage();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const save = async (patch: LaunchSettingsPatch): Promise<boolean> => {
    setBusy(true);
    setFailure(null);
    try {
      const saved = await unwrap(api.PUT("/v1/launch-settings", { body: patch }));
      queryClient.setQueryData(launchSettingsKey(wsId), saved);
      toast.success(savedMessage);
      return true;
    } catch (caught) {
      setFailure(message(caught));
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { save, busy, failure, clearFailure: () => { setFailure(null); } };
}

/** `POST /v1/launch-settings/test-launch` (E18): mints a sandbox launch for a made-up customer. */
export function useStartSandboxLaunch() {
  const api = useApi();
  return useMutation({ mutationFn: (body: SandboxLaunchIn) => unwrap(api.POST("/v1/launch-settings/test-launch", { body })) });
}

/** `GET /v1/launch-settings/test-launch/{launch_id}` (E19), polled every 2 s while the code waits to be redeemed. */
export function useSandboxLaunchStatus(launchId: string | null) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: sandboxKey(wsId, launchId ?? "none"),
    queryFn: () => unwrap(api.GET("/v1/launch-settings/test-launch/{launch_id}", { params: { path: { launch_id: launchId ?? "" } } })),
    enabled: launchId !== null,
    retry: false,
    refetchInterval: (query) => (query.state.status !== "error" && (query.state.data === undefined || query.state.data.status === "issued") ? POLL_MS : false),
  });
}
